import { IAdminSettingSection } from 'api/types';
import { AdminSectionSettingCodes as codes } from 'const';
import { IRootState } from 'reducers';
import { createSelector } from 'reselect';
import { adminSettingsSelector } from 'selectors';
import { isDefined } from 'utils';

import { proforientationApplicationSelectors } from './applications';
import { proforientationEventSelectors } from './events';
import proforientationResultLinkSelector from './proforientationResultLinkSelector';
import { proforientationRecommendationSelectors } from './recommendations';

const adminVisible = (settings: IAdminSettingSection[], sectionId: number) => {
  const section = settings.find((section) => section.sectionId === sectionId);
  return section?.isVisible ?? true;
};

const overwiew = createSelector([proforientationResultLinkSelector], (resultLink) => {
  return isDefined(resultLink);
});

const applications = createSelector(
  [
    proforientationApplicationSelectors.probes,
    proforientationApplicationSelectors.excursions,
    proforientationApplicationSelectors.dods,
  ],
  (probes, excursions, dods) => {
    return {
      probes: probes.length > 0,
      excursions: excursions.length > 0,
      dods: dods.length > 0,
    };
  },
);

const recommendations = createSelector(
  [
    proforientationRecommendationSelectors.proforientation,
    proforientationRecommendationSelectors.probes,
    proforientationRecommendationSelectors.excursions,
    proforientationRecommendationSelectors.dods,
    (state: IRootState) => adminSettingsSelector(state),
    (state: IRootState) => state.proforientation,
    proforientationApplicationSelectors.probes,
  ],
  (proforientation, probes, excursions, dods, adminSettings, proforientationResults, probesApplications) => {
    const proforientationHasData = proforientation.length > 0;
    const probesHasData = probes.length > 0;
    const excursionsHasData = excursions.length > 0;
    const dodsHasData = dods.length > 0;

    const s = adminSettings.content.sections;
    const proforientationVisible = adminVisible(s, codes.professionProforientationRecommentationsProforientation);
    const probesVisible = adminVisible(s, codes.professionProforientationRecommentationsProbes);
    const excursionsVisible = adminVisible(s, codes.professionProforientationRecommentationsExcursions);
    const dodsVisible = adminVisible(s, codes.professionProforientationRecommentationsDods);

    const visitedProbes =
      proforientationResults.content?.events?.history?.data?.masterstvo?.filter((probe) => probe.visited) ?? [];
    const acceptedProbesApplications = probesApplications.filter((application) => application.statusApplication === 1);
    // Если сумма посещенных и одобренных проб равна 3,
    // то учащемуся нужно отобразить баннер, что он посетил все доступные ему пробы
    const showProbesOverLimitBanner = acceptedProbesApplications.length + visitedProbes.length >= 3;
    return {
      proforientation: proforientationHasData && proforientationVisible,
      probes: (probesHasData || showProbesOverLimitBanner) && probesVisible,
      excursions: excursionsHasData && excursionsVisible,
      dods: dodsHasData && dodsVisible,
    };
  },
);

const events = createSelector(
  [
    proforientationEventSelectors.probes,
    proforientationEventSelectors.excursions,
    proforientationEventSelectors.dods,
    (state: IRootState) => adminSettingsSelector(state),
  ],
  (probes, excursions, dods, adminSettings) => {
    const s = adminSettings.content.sections;
    const probesVisible = adminVisible(s, codes.professionProforientationEventsProbes);
    const excursionsVisible = adminVisible(s, codes.professionProforientationEventsExcursions);
    const dodsVisible = adminVisible(s, codes.professionProforientationEventsDods);

    return {
      probes: probes.length > 0 && probesVisible,
      excursions: excursions.length > 0 && excursionsVisible,
      dods: dods.length > 0 && dodsVisible,
    };
  },
);

export const proforientationVisibilitySelectors = {
  overwiew,
  applications,
  recommendations,
  events,
};
